import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAlert, formatDate } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import { api } from '../lib/api';
import type { Alert, AlertPatch } from '../lib/phase1';
import { Button, Card, EmptyState, Pill, Skeleton } from '../components/ui';
import { InfoNote, ReasonModal } from '../components/compliance';
import { FileText, ShieldAlert, Flag, TriangleAlert } from 'lucide-react';

const FLOW = ['Generada', 'Asignada', 'En Investigación', 'Resuelta'];

/** Free-form backend estado → spine index (the "happy path"). */
function flowIndex(estado: string): number {
  if (estado === 'Generada') return 0;
  if (estado === 'Asignada') return 1;
  if (estado === 'En Investigación') return 2;
  return 3;
}

/** Resolved = the case left the active investigation funnel. */
function isResolved(estado: string): boolean {
  return (
    estado.includes('Resuelta') ||
    estado.includes('Desestimada') ||
    estado.includes('Escalada') ||
    estado.includes('ROS')
  );
}

export default function AlertaDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: alert, isLoading, isError } = useAlert(id);

  if (isLoading) {
    return (
      <div>
        <button
          onClick={() => navigate('/alertas')}
          className="mb-3 font-mono text-[11px] text-ink-soft hover:text-ink"
        >
          ← Volver a alertas
        </button>
        <Card className="mb-4">
          <Skeleton className="h-20 w-full" />
        </Card>
        <Card>
          <Skeleton className="h-32 w-full" />
        </Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <button
          onClick={() => navigate('/alertas')}
          className="mb-3 font-mono text-[11px] text-ink-soft hover:text-ink"
        >
          ← Volver a alertas
        </button>
        <Card>
          <EmptyState
            icon={<TriangleAlert className="h-8 w-8" strokeWidth={1.5} />}
            title="No se pudo cargar el caso"
            hint="Ocurrió un error al consultar la alerta. Vuelve a intentarlo en unos momentos."
          />
        </Card>
      </div>
    );
  }

  if (!alert) {
    return (
      <div>
        <button
          onClick={() => navigate('/alertas')}
          className="mb-3 font-mono text-[11px] text-ink-soft hover:text-ink"
        >
          ← Volver a alertas
        </button>
        <Card>
          <EmptyState
            icon={<ShieldAlert className="h-8 w-8" strokeWidth={1.5} />}
            title="Caso no encontrado"
            hint="La alerta no existe o ya no está disponible."
          />
        </Card>
      </div>
    );
  }

  return <CaseView alert={alert} />;
}

interface Modal {
  title: string;
  confirm: string;
  reasons: string[];
  tone?: 'default' | 'destructive';
  /** estado to PATCH the alert to. */
  resolveTo: string;
  /** Short code persisted to resolutionCode (the regulator-facing reason class). */
  code: string;
}

interface BitacoraEntry {
  ts: string;
  who: string;
  text: string;
}

/** Build the immutable bitácora from the alert's persisted fields. */
function buildBitacora(alert: Alert): BitacoraEntry[] {
  const entries: BitacoraEntry[] = [];
  if (alert.abierta) {
    entries.push({
      ts: formatDate(alert.abierta, alert.abierta),
      who: 'Motor de reglas',
      text: `Alerta generada — ${alert.tipo}`,
    });
  }
  if (alert.asignada) {
    entries.push({
      ts: '—',
      who: alert.asignada,
      text: 'Caso asignado para investigación',
    });
  }
  if (alert.resuelta) {
    const motivo = alert.codigoResolucion
      ? `${alert.codigoResolucion}${alert.notaResolucion ? ` — ${alert.notaResolucion}` : ''}`
      : (alert.notaResolucion ?? 'sin motivo registrado');
    entries.push({
      ts: formatDate(alert.resuelta, alert.resuelta),
      who: alert.asignada ?? 'Cumplimiento',
      text: `Caso cerrado (${alert.estado}) — motivo: ${motivo}`,
    });
  }
  // Newest first.
  return entries.reverse();
}

function prioridadTone(prioridad: Alert['prioridad']) {
  return prioridad === 'Alta' ? 'red' : prioridad === 'Media' ? 'amber' : 'slate';
}

function CaseView({ alert }: { alert: Alert }) {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [modal, setModal] = useState<Modal | null>(null);

  const estado = alert.estado;
  const idx = flowIndex(estado);
  const resolved = isResolved(estado);
  const bitacora = buildBitacora(alert);

  const mutation = useMutation({
    mutationFn: (patch: AlertPatch) => api.updateAlert(alert.id, patch),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['alertas'] });
      await qc.invalidateQueries({ queryKey: ['alertas', alert.id] });
    },
  });

  function apply(m: Modal, reason: string) {
    mutation.mutate(
      {
        estado: m.resolveTo,
        resolutionCode: m.code,
        resolutionNote: reason,
      },
      {
        onSuccess: () => toast({ message: `Caso ${m.confirm.toLowerCase()}`, kind: 'success' }),
        onError: () =>
          toast({ message: 'No se pudo actualizar el caso. Inténtalo de nuevo.', kind: 'error' }),
      }
    );
  }

  function markForRos() {
    mutation.mutate(
      { estado: 'Marcada para ROS' },
      {
        onSuccess: () =>
          toast({ message: 'Caso marcado para ROS — pendiente de Gerencia', kind: 'success' }),
        onError: () =>
          toast({ message: 'No se pudo marcar el caso para ROS.', kind: 'error' }),
      }
    );
  }

  const busy = mutation.isPending;

  return (
    <div>
      <button
        onClick={() => navigate('/alertas')}
        className="mb-3 font-mono text-[11px] text-ink-soft hover:text-ink"
      >
        ← Volver a alertas
      </button>

      <Card className="mb-4">
        <div className="flex flex-wrap items-start gap-4">
          <span className="grid h-12 w-12 place-items-center rounded-2xl bg-red-100">
            <ShieldAlert className="h-6 w-6 text-red-600" strokeWidth={1.75} />
          </span>
          <div>
            <h1 className="font-serif text-2xl font-medium">{alert.tipo}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-xs text-ink-soft">
              <span className="font-mono text-ink-faint">{alert.id}</span>
              <span>·</span>
              <span className="font-semibold text-ink">{alert.cliente}</span>
              <Pill tone={prioridadTone(alert.prioridad)}>Prioridad {alert.prioridad}</Pill>
              <Pill tone={resolved ? 'green' : estado === 'Generada' ? 'slate' : 'blue'}>{estado}</Pill>
            </div>
          </div>
          <div className="ml-auto flex flex-wrap gap-2">
            {alert.expedienteId && (
              <Button variant="outline" onClick={() => navigate(`/revision/${alert.expedienteId}`)}>
                <FileText className="h-4 w-4" strokeWidth={1.75} /> Ver expediente
              </Button>
            )}
            <Button
              variant="outline"
              disabled={resolved || busy}
              onClick={() =>
                setModal({
                  title: 'Desestimar alerta',
                  confirm: 'Desestimada',
                  reasons: ['Falso positivo', 'Riesgo no material', 'Alerta duplicada', 'Resuelta con el cliente'],
                  tone: 'destructive',
                  resolveTo: 'Desestimada',
                  code: 'Desestimada',
                })
              }
            >
              Desestimar
            </Button>
            <Button
              disabled={resolved || busy}
              onClick={() =>
                setModal({
                  title: 'Escalar a gerencia',
                  confirm: 'Escalada',
                  reasons: ['Riesgo alto', 'Posible ROS', 'Requiere decisión gerencial'],
                  resolveTo: 'Escalada → Gerencia',
                  code: 'Escalada a Gerencia',
                })
              }
            >
              Escalar
            </Button>
          </div>
        </div>

        {/* alert state track */}
        <div data-tour="alerta-lifecycle" className="mt-5 flex flex-wrap items-center gap-1 rounded-xl bg-canvas p-3.5">
          {FLOW.map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <div className="flex min-w-[84px] flex-col items-center gap-1.5">
                <span
                  className={
                    'h-3.5 w-3.5 rounded-full border-2 ' +
                    (i < idx
                      ? 'border-primary bg-primary'
                      : i === idx
                      ? 'border-primary bg-panel ring-4 ring-primary/15'
                      : 'border-line-strong bg-panel')
                  }
                />
                <span
                  className={
                    'text-[10.5px] font-semibold ' +
                    (i === idx ? 'text-ink' : i < idx ? 'text-primary-dark' : 'text-ink-faint')
                  }
                >
                  {i === 3 && resolved ? estado : s}
                </span>
              </div>
              {i < FLOW.length - 1 && (
                <span className={'-mt-4 h-0.5 w-5 ' + (i < idx ? 'bg-primary' : 'bg-line-strong')} />
              )}
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <div className="grid content-start gap-4">
          {alert.descripcion && (
            <Card>
              <h3 className="mb-2 text-sm font-bold">Detalle de la alerta</h3>
              <p className="text-sm text-ink-soft">{alert.descripcion}</p>
            </Card>
          )}

          <Card>
            <h3 className="mb-3 text-sm font-bold">Bitácora de investigación</h3>
            {bitacora.length === 0 ? (
              <p className="text-sm text-ink-faint">Sin eventos registrados todavía.</p>
            ) : (
              <div className="grid gap-0 font-mono text-xs">
                {bitacora.map((n, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-[120px_1fr] gap-3 border-b border-line py-3 last:border-0"
                  >
                    <div className="text-[10.5px] text-ink-faint">{n.ts}</div>
                    <div>
                      <span className="text-primary-dark">{n.who}</span>{' '}
                      <b className="font-semibold text-ink">{n.text}</b>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="grid content-start gap-4">
          <Card>
            <h3 className="mb-3 text-sm font-bold">Reporte de Operación Sospechosa</h3>
            <p className="mb-4 text-sm text-ink-soft">
              Si la investigación lo amerita, genera el ROS pre-poblado desde este caso para revisión de Gerencia y
              envío a la UAF.
            </p>
            <Button
              variant="outline"
              className="w-full"
              disabled={resolved || busy}
              onClick={markForRos}
            >
              <Flag className="h-4 w-4" strokeWidth={1.75} /> Marcar para ROS
            </Button>
          </Card>
          <InfoNote tone="blue">
            🔁
            <div>
              Cada descarte o escalamiento exige un motivo y queda en la bitácora inmutable — el regulador puede
              preguntar por qué se cerró una alerta.
            </div>
          </InfoNote>
        </div>
      </div>

      {modal && (
        <ReasonModal
          open
          title={modal.title}
          confirmLabel={modal.confirm}
          reasons={modal.reasons}
          tone={modal.tone}
          onClose={() => setModal(null)}
          onConfirm={(reason) => {
            const m = modal;
            setModal(null);
            apply(m, reason);
          }}
        />
      )}
    </div>
  );
}
